import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip, ZoomControl } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { motion } from 'framer-motion'; 
import { Globe, Users, MapPin, User, Filter } from 'lucide-react';
import { supabase } from '../lib/supabase';

// Special marker for Edward's Hamlet - only showing city/country level data
const EDWARDS_HAMLET = {
  city: "Jinja",
  country: "Uganda",
  count: 275, 
  isHybridHub: true
};

interface UserLocation {
  latitude?: number;
  longitude?: number;
  city?: string;
  country?: string;
  count: number;
  isHybridHub?: boolean;
  userName?: string;
}

const UserDistributionMap: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [userLocations, setUserLocations] = useState<UserLocation[]>([]);
  const [viewMode, setViewMode] = useState<'aggregated' | 'individual'>('aggregated');
  
  // Demo data - not actual user locations
  const sampleLocations: UserLocation[] = [
    { ...EDWARDS_HAMLET }, // Edward's Hamlet as our first Hybrid farm hub
    { city: 'London', country: 'United Kingdom', count: 12 },
    { city: 'Kampala', country: 'Uganda', count: 8 },
    { city: 'New York', country: 'United States', count: 15 },
    { city: 'San Francisco', country: 'United States', count: 10 },
    { city: 'Paris', country: 'France', count: 7 },
    { city: 'Nairobi', country: 'Kenya', count: 9 },
    { city: 'Sydney', country: 'Australia', count: 6 },
    { city: 'New Delhi', country: 'India', count: 11 }
  ];

  // Individual user data - for demonstration purposes
  const individualUserData: UserLocation[] = [
    { city: "Jinja", country: "Uganda", count: 1, userName: "Edward", isHybridHub: true },
    { city: "London", country: "United Kingdom", count: 1, userName: "Sarah" }, 
    { city: "Kampala", country: "Uganda", count: 1, userName: "David" }, 
    { city: "New York", country: "United States", count: 1, userName: "Michael" }, 
    { city: "San Francisco", country: "United States", count: 1, userName: "Jennifer" }, 
    { city: "Paris", country: "France", count: 1, userName: "Pierre" }, 
    { city: "Nairobi", country: "Kenya", count: 1, userName: "Amara" }, 
    { city: "Sydney", country: "Australia", count: 1, userName: "James" }, 
    { city: "New Delhi", country: "India", count: 1, userName: "Priya" }
  ];
  
  // Calculate total users (including all farmers in Edward's Hamlet)
  const totalUsers = sampleLocations.reduce((sum, location) => sum + location.count, 0);
  const countriesCount = new Set(sampleLocations.map(loc => loc.country)).size;
  
  useEffect(() => {
    // Set the locations based on view mode
    setUserLocations(viewMode === 'aggregated' ? sampleLocations : individualUserData);
  }, [viewMode]);

  const getMarkerSize = (count: number) => {
    // Scale marker size based on count, with a minimum size
    if (viewMode === 'individual') {
      return 6; // Fixed size for individual users
    } else {
      return Math.max(8, Math.min(20, 8 + Math.log2(count) * 2));
    }
  };

  // City coordinates mapping for privacy (approximate city centers, not exact user locations)
  const getCityCoordinates = (city: string, country: string): [number, number] => {
    const cityMap: Record<string, [number, number]> = {
      'Jinja': [0.44, 33.20],
      'London': [51.50, -0.12],
      'Kampala': [0.34, 32.58],
      'New York': [40.71, -74.00],
      'San Francisco': [37.77, -122.41],
      'Paris': [48.85, 2.35],
      'Nairobi': [-1.29, 36.82],
      'Sydney': [-33.86, 151.20],
      'New Delhi': [28.61, 77.20],
      'Berlin': [52.52, 13.40],
      'Tokyo': [35.68, 139.76],
      'Rio de Janeiro': [-22.91, -43.17],
      'Cape Town': [-33.92, 18.42],
      'Toronto': [43.65, -79.38],
      'Mexico City': [19.43, -99.13]
    };
    
    // Add small random offset for individual view to prevent exact overlaps
    const coordinates = cityMap[city] || [0, 0];
    
    if (viewMode === 'individual') {
      // Add a small random offset (±0.05 degrees) for visual separation
      return [
        coordinates[0] + (Math.random() * 0.1 - 0.05),
        coordinates[1] + (Math.random() * 0.1 - 0.05)
      ];
    }
    
    return coordinates;
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 overflow-hidden">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3 flex-wrap">
          <motion.div
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 4, repeat: Infinity }}
            className="bg-avn-purple-100 p-2 rounded-lg"
          >
            <Globe className="h-6 w-6 text-avn-purple-600" />
          </motion.div>
          <h2 className="text-2xl font-semibold">Global User Distribution</h2>
        </div>
        
        <div className="flex gap-4">
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('aggregated')}
              className={`px-3 py-1 rounded-md flex items-center gap-1 ${
                viewMode === 'aggregated' 
                  ? 'bg-avn-purple-600 text-white' 
                  : 'text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Users className="h-4 w-4" />
              <span className="text-sm">Groups</span>
            </button>
            <button
              onClick={() => setViewMode('individual')}
              className={`px-3 py-1 rounded-md flex items-center gap-1 ${
                viewMode === 'individual' 
                  ? 'bg-avn-purple-600 text-white' 
                  : 'text-gray-600 hover:bg-gray-200'
              }`}
            >
              <User className="h-4 w-4" />
              <span className="text-sm">Individual</span>
            </button>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-avn-purple-600" />
              <div>
                <div className="text-2xl font-bold">{totalUsers}</div>
                <div className="text-xs text-gray-500">Active Users</div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-avn-purple-600" />
              <div>
                <div className="text-2xl font-bold">{countriesCount}</div>
                <div className="text-xs text-gray-500">Countries</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="relative h-[500px] rounded-lg overflow-hidden">
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <div className="flex flex-col items-center gap-3">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-avn-purple-600"></div>
              <p className="text-gray-600">Loading user distribution data...</p>
            </div>
          </div>
        ) : (
          <MapContainer 
            center={[20, 0]} 
            zoom={2} 
            style={{ height: '100%', width: '100%' }}
            zoomControl={false}
            attributionControl={false}
          >
            <TileLayer 
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            <ZoomControl position="bottomright" />
            
            {userLocations.map((location, index) => {
              // Get city coordinates from our privacy-preserving map
              const [lat, lng] = getCityCoordinates(location.city || '', location.country || '');
              
              return (
                <CircleMarker
                  key={index}
                  center={[lat, lng]}
                  radius={getMarkerSize(location.count)}
                  pathOptions={{ 
                    fillColor: location.isHybridHub ? '#16a34a' : '#9333ea', 
                    fillOpacity: location.isHybridHub ? 0.8 : 0.6, 
                    color: location.isHybridHub ? '#15803d' : '#7e22ce',
                    weight: location.isHybridHub ? 2 : 1
                  }}
                >
                  <Tooltip>
                    <div className="font-medium">
                      {viewMode === 'individual' && location.userName && (
                        <span className="font-bold">{location.userName}</span>
                      )}
                      {viewMode === 'individual' && location.userName && <span> • </span>}
                      {location.city || 'Unknown'}, {location.country || 'Unknown'}
                      {location.isHybridHub && <span className="text-green-600"> • Hybrid Farm Hub</span>}
                    </div>
                    <div className="text-sm">
                      {viewMode === 'aggregated' ? (
                        <>
                          {location.isHybridHub 
                            ? '275 member farmers' 
                            : `${location.count} ${location.count === 1 ? 'user' : 'users'}`}
                        </>
                      ) : (
                        location.isHybridHub && '275 member farmers'
                      )}
                    </div>
                  </Tooltip>
                </CircleMarker>
              );
            })}
          </MapContainer>
        )}
      </div>
      
      <div className="mt-4 text-sm text-gray-500 text-center">
        <p>
          {viewMode === 'aggregated' 
            ? 'Demonstration map showing aggregated user distribution by city, including Edward\'s Hamlet with 275 farmers.' 
            : 'Demonstration map showing 9 individual users plus Edward\'s Hamlet with 275 farmers.'}
        </p>
        <p className="text-xs mt-1 text-gray-400">
          Note: For privacy protection, only city-level location data is displayed, 
          not precise GPS coordinates.
        </p>
      </div>
    </div>
  );
};

export default UserDistributionMap;